import _ from 'lodash'
import moment from 'moment'

const isPending = order => {
  return (
    order.status === 'pending' || order.status === 'confirmed' || order.status === 'drivers_not_available'
  )
}

const isAttention = order => {
  return (
    isPending(order) &&
    !order.deliverer &&
    (order.delivery_time === undefined || order.delivery_time === 'ETA')
  )
}

const isSchedule = order => {
  return (
    isPending(order) &&
    !order.deliverer &&
    (order.delivery_time !== undefined && order.delivery_time !== 'ETA')
  )
}

const getOrderStatus = order => {
  if (isAttention(order)) return 'attention'

  if (isSchedule(order)) return 'scheduled'

  if (order.status === 'drivers_not_available') return 'confirmed'

  if (['confirmed', 'outForDelivery', 'rejected', 'delivered'].includes(order.status)) return order.status

  return null
}

const hasAttentionScheduleOrders = orders => {
  return orders.some(order => {
    const schedule = moment(order.delivery_time)
    const shouldOrder = schedule.subtract(parseInt(order.order_took_eta, 10), 'minutes')

    return moment().isSameOrAfter(shouldOrder)
  })
}

const getOrderNumber = orderData => {
  let phone = orderData.tel || ''
  let orderKey = orderData.key

  phone = phone.substring(phone.length - 4, phone.length)

  orderKey = orderKey.substring(orderKey.length - 3, orderKey.length).toUpperCase()

  const mergedOrderId = `#${phone}${orderKey}`

  return mergedOrderId
}

const getExpressOrderNumber = (phone, id) => {
  // eslint-disable-next-line no-param-reassign
  phone = phone.substring(phone.length - 4, phone.length)
  // eslint-disable-next-line no-param-reassign
  id = id.substring(id.length - 3, id.length).toUpperCase()

  const mergedOrderId = `#${phone}${id}`

  return mergedOrderId
}

const millisecondsToMinute = time => {
  return parseInt(time / 60000, 10)
}

const convertToNumber = amount => {
  return Number(parseFloat(amount).toFixed(2))
}

const roundRiel = amount => {
  let rielAmount = Number(amount)
  const amountInString = rielAmount.toString()

  const lastTwoDigits = Number(amountInString.substring(amountInString.length - 2, amountInString.length))

  rielAmount -= lastTwoDigits

  const lastTwoDigitsRound = lastTwoDigits < 50 ? 0 : 100

  rielAmount += lastTwoDigitsRound

  return Number(rielAmount)
}

const convertToRiel = (amount, rielRate = 4100) => {
  const rielAmount = roundRiel(Number(amount * rielRate))

  return rielAmount.toLocaleString('km')
}

const convertFromCent = amount => {
  return convertToNumber(amount / 100)
}

const getOrderDiscount = order => {
  let voucherDiscount = 0
  let companyDiscount = 0
  let merchantDiscount = order.promotion_discount || 0
  const deliveryDiscount = order.delivery_discount || 0

  if (order.voucher) {
    const { voucher } = order.voucher
    const isDiscountVoucher = voucher.type === 'DISCOUNT_VOUCHER'

    if (isDiscountVoucher) {
      voucherDiscount = convertFromCent(order.voucher.order.discount_amount)
    } else {
      voucherDiscount = convertFromCent(order.voucher.amount)
    }
  }

  const { cost_sharing: costSharing } = _.get(order, 'restaurant.branch.discount', {})

  if (order.cost_sharing) {
    companyDiscount = _.get(order, 'cost_sharing.company_discount', 0)
    merchantDiscount = _.get(order, 'cost_sharing.merchant_discount', 0)
  }

  return {
    costSharing,
    merchantDiscount,
    companyDiscount,
    voucherDiscount,
    deliveryDiscount,
  }
}

const getDynamicFeeAmount = fee => {
  if (fee.type === 'FIXED') {
    return fee.fee_amount
  }

  // if (fee.type === 'PERCENT') {
  //   const amount = order.convertToNumber(amountToMultiply * (fee.fee_rate / 100))
  //   return amount
  // }

  return 0
}

const serializeDynamicFees = (dynamicFees = {}) => {
  const driverFees = { totalAmount: 0, fees: [] }
  const merchantFees = { totalAmount: 0, fees: [] }
  const companyFees = { totalAmount: 0, fees: [] }
  const allFees = { totalAmount: 0, fees: [] }

  const feeKeys = Object.keys(dynamicFees || {})

  if (feeKeys.length > 0) {
    for (let i = 0; i < feeKeys.length; i += 1) {
      const feeKey = feeKeys[i]
      const fee = dynamicFees[feeKey]

      const feeAmount = getDynamicFeeAmount(fee)

      allFees.totalAmount = convertToNumber(allFees.totalAmount + feeAmount)
      allFees.fees.push(fee)

      if (fee.go_to === 'DRIVER') {
        driverFees.totalAmount = convertToNumber(driverFees.totalAmount + feeAmount)
        driverFees.fees.push(fee)
      }

      if (fee.go_to === 'MERCHANT') {
        merchantFees.totalAmount = convertToNumber(merchantFees.totalAmount + feeAmount)
        merchantFees.fees.push(fee)
      }

      if (fee.go_to === 'COMPANY') {
        companyFees.totalAmount = convertToNumber(companyFees.totalAmount + feeAmount)
        companyFees.fees.push(fee)
      }
    }
  }

  return { allFees, driverFees, merchantFees, companyFees }
}

const getOrderSummary = order => {
  if (!order) return {}

  const subTotal = Number(order.sub_total)
  const deliveryFee = Number(order.delivery_fee)
  let dynamicFees = _.get(order, 'restaurant.branch.dynamic_fees', {})
  dynamicFees = serializeDynamicFees(dynamicFees)

  const distance = convertToNumber(_.get(order, 'etaInfo.info.legs.0.distance.value', 0) / 1000)

  const serverTime = Number(moment().format('x')) // milliseconds
  const etaTime = Number(order.created_at + order.order_took_eta * 60000) // milliseconds
  const scheduleTime = order.delivery_time !== 'ETA' ? order.delivery_time : 0
  const arrivalTime = scheduleTime || etaTime
  const remainingTime = Number(arrivalTime - serverTime) // milliseconds

  const {
    merchantDiscount,
    voucherDiscount,
    companyDiscount,
    costSharing,
    deliveryDiscount,
  } = getOrderDiscount(order)
  const customerDiscount = merchantDiscount + voucherDiscount + companyDiscount + deliveryDiscount
  const subTotalAfterMerchantDiscount = subTotal - merchantDiscount

  const vatRate = _.get(order, 'restaurant.vat', 0)
  const vatFee = convertToNumber(subTotal * (vatRate / 100))

  const customerTotal = convertToNumber(subTotal - customerDiscount)

  const merchantSettlementType = _.get(order, 'restaurant.branch.merchant_settlement_type', 'net')
  const commissionRate = merchantSettlementType === 'net' ? Number(order.discount_rate) : 0
  const commissionFee = convertToNumber(subTotal * (commissionRate / 100))

  const customerGrandTotal = convertToNumber(
    customerTotal + deliveryFee + vatFee + dynamicFees.allFees.totalAmount
  )

  const merchantTotal = convertToNumber(subTotalAfterMerchantDiscount + vatFee)
  const merchantGrandTotal = convertToNumber(
    merchantTotal - commissionFee + dynamicFees.merchantFees.totalAmount
  )

  return {
    vatFee,
    vatRate,
    distance,
    subTotal,
    deliveryFee,
    commissionFee,
    voucherDiscount,
    costSharing,
    companyDiscount,
    merchantDiscount,
    customerDiscount,
    customerTotal,
    customerGrandTotal,
    merchantTotal,
    merchantGrandTotal,
    arrivalTime,
    orderNumber: getOrderNumber(order),
    remainingTime: millisecondsToMinute(remainingTime),
    merchantName: _.get(order, 'restaurant.name', '--'),
    branchName: _.get(order, 'restaurant.branch.name', '--'),
    status: _.get(order, 'status', null),
    customerName: order.deliveree_name,
    deliveryNote: order.note,
    basedOrder: order,
    dynamicFees,
    deliveryDiscount,
    merchantSettlementType,
  }
}

const calculateCustomerGrandTotal = ({
  subtotal,
  deliveryFee,
  merchantVatRate,
  dynamicFees,
  voucherDiscountAmount,
  promotionDiscount,
  deliveryDiscount,
}) => {
  const parsedSubtotal = Number(subtotal)
  const customerTotal = promotionDiscount + voucherDiscountAmount + deliveryDiscount
  const parsedDeliveryFee = Number(deliveryFee)

  const dynamicFeeAmount =
    _.chain(dynamicFees)
      .values()
      .sumBy(df => getDynamicFeeAmount(df))
      .value() || 0

  const vatFee = convertToNumber(parsedSubtotal * (merchantVatRate / 100))
  return convertToNumber(parsedSubtotal - customerTotal + parsedDeliveryFee + vatFee + dynamicFeeAmount)
}

const getItemOptionAmount = ({ options = {} }) =>
  _.reduce(
    options,
    (result, option) => {
      _.forEach(option.selected_choices, choice => {
        result += Number(choice.price)
      })

      return result
    },
    0
  )

const getOrderSubTotal = items => {
  let subTotal = 0

  _.map(items, item => {
    const itemOptionAmount = getItemOptionAmount(item)
    const itemUnitPrice = Number(item.price_at_order) + itemOptionAmount
    const itemTotalAmount = itemUnitPrice * Number(item.quantity)

    subTotal += Number(itemTotalAmount)
  })

  return convertToNumber(subTotal)
}

const getItemDiscountRate = (itemKey, branchDiscount, specialDeal) => {
  if (!branchDiscount && !specialDeal) return { discountRate: 0 }

  let highestDiscountRate = 0
  let itemCostSharing = null

  if (branchDiscount) {
    const {
      costSharing,
      discountRate,
      selectedItems,
      customItemDiscountRate,
      discountOnAllItems,
    } = branchDiscount

    const isDiscount = discountOnAllItems || selectedItems.includes(itemKey)
    const customDiscountRate = customItemDiscountRate[itemKey]
    highestDiscountRate = isDiscount ? customDiscountRate || discountRate : 0
    itemCostSharing = costSharing
  }

  highestDiscountRate =
    specialDeal && specialDeal.discount_rate >= highestDiscountRate
      ? specialDeal.discount_rate
      : highestDiscountRate

  itemCostSharing =
    specialDeal && specialDeal.discount_rate >= highestDiscountRate
      ? specialDeal.cost_sharing
      : itemCostSharing

  return {
    discountRate: highestDiscountRate,
    costSharing: highestDiscountRate > 0 ? itemCostSharing : null,
  }
}

const serializedBranchPromotion = promotion => {
  const {
    custom,
    started_at: startedAt,
    cost_sharing: costSharing,
    discount_rate: discountRate,
    selected_items: selectedItems,
  } = promotion

  const now = Number(moment().format('x'))

  if (startedAt > now) return null

  const customItemDiscountRate = {}

  if (custom) {
    custom.forEach(customPromo => {
      customPromo.item_ids.forEach(itemId => {
        customItemDiscountRate[itemId] = customPromo.discount_rate
      })
    })
  }

  const hasCustomDiscount = _.size(customItemDiscountRate) > 0
  const highesDiscountRate = hasCustomDiscount ? _.max(Object.values(customItemDiscountRate)) : 0

  return {
    costSharing,
    discountRate,
    selectedItems,
    customItemDiscountRate,
    discountOnAllItems: !selectedItems,
    hasCustomDiscount: _.size(customItemDiscountRate) > 0,
    highesDiscountRate: highesDiscountRate > discountRate ? highesDiscountRate : discountRate,
  }
}

const getOrderSubtotalWithDiscount = (items, branchDiscount, specialDealItems, promotionEligible = true) => {
  const promotion = branchDiscount && serializedBranchPromotion(branchDiscount)
  const selectedItems = {}
  let subTotalAfterDiscount = 0
  let companyDiscount = 0
  let merchantDiscount = 0

  _.forEach(items, item => {
    const itemOptionAmount = getItemOptionAmount(item)
    const specialDeal = specialDealItems[item.item_key]
    const { discountRate, costSharing } = getItemDiscountRate(item.item_key, promotion, specialDeal)

    const itemUnitPrice = Number(item.price_at_order) + itemOptionAmount
    let discountAmount = 0

    if (discountRate > 0 && promotionEligible) {
      discountAmount = (discountRate / 100) * itemUnitPrice
      selectedItems[item.item_key] = discountRate
    }

    discountAmount *= Number(item.quantity)
    const itemTotalAmount = itemUnitPrice * Number(item.quantity)

    companyDiscount += costSharing ? (costSharing.company / discountRate) * discountAmount : 0
    merchantDiscount += costSharing ? (costSharing.merchant / discountRate) * discountAmount : discountAmount

    subTotalAfterDiscount += Number(itemTotalAmount) - Number(discountAmount)
  })

  const costSharing =
    companyDiscount > 0
      ? {
          company_discount: convertToNumber(companyDiscount),
          merchant_discount: convertToNumber(merchantDiscount),
        }
      : null

  return {
    costSharing,
    subTotalAfterDiscount: convertToNumber(subTotalAfterDiscount),
    selectedItems: _.size(selectedItems) > 0 ? selectedItems : null,
  }
}

const getGroupOrderSubTotal = items => {
  let subTotal = 0

  _.map(items, item => {
    const itemPrice = Number(item.price_at_order)

    const itemTotalAmount = _.sum(_.map(item.belong_to, userItem => itemPrice * Number(userItem.quantity)))

    subTotal += Number(itemTotalAmount)
  })

  return convertToNumber(subTotal)
}

const extractGroupOrderItems = items => {
  const newItems = {}

  _.forEach(items, (item, itemKey) => {
    let concatNote = ''
    let quantity = 0

    _.forEach(item.belong_to, userItem => {
      const userName = userItem.name
      const userQty = Number(userItem.quantity)
      const specialNote = userItem.special_note || ''

      concatNote += specialNote ? `${userName} notes:\n${`${specialNote}\n\n` || ''}` : ''
      quantity += userQty
    })

    newItems[itemKey] = {
      ...item,
      quantity,
      price_at_order: Number(item.price_at_order),
      special_note: concatNote,
    }
  })

  return newItems
}

const getOrderPromotionDiscount = (subTotal, order) => {
  const discount = subTotal * (order.restaurant.branch.discount.discount_rate / 100)

  return convertToNumber(discount)
}

const countOrderItems = items => {
  return _.sum(_.map(items, item => Number(item.quantity)))
}

const countGroupOrderItems = items => {
  return _.sum(_.map(items, item => _.sum(_.map(item.belong_to, userItem => Number(userItem.quantity)))))
}

const getLatency = (orderData, eta) => {
  const startAt = moment(parseInt(orderData.created_at, 10))
  const current = moment(parseInt(orderData.updated_at, 10))

  const durationFromNowToStartAt = moment.duration(current.diff(startAt)).as('minutes')

  const remaining = eta - parseInt(durationFromNowToStartAt, 10)

  return remaining
}

const getOrderItems = orderData => {
  const items = _.map(orderData.items, item => {
    return {
      name: item.name,
      quantity: item.quantity,
    }
  })

  return items
}

const getEta = (orderData, restaurant) => {
  let durationOfDistance = 45
  let branchCookTime = 15
  const findingDriver = 5
  const callingPartner = 5

  if (orderData.etaInfo.info.legs[0].duration_in_traffic !== undefined) {
    durationOfDistance = parseInt(orderData.etaInfo.info.legs[0].duration_in_traffic.value / 60, 10)
  } else {
    durationOfDistance = parseInt(orderData.etaInfo.info.legs[0].duration.value / 60, 10)
  }

  if (restaurant && restaurant.cooking_duration) {
    branchCookTime = restaurant.cooking_duration
  }

  const totalETA = durationOfDistance + findingDriver + Number(branchCookTime) + callingPartner

  return totalETA
}

const getOrderCommissionFee = orderData => {
  const subTotal = Number(orderData.sub_total)
  const commissionRate = Number(orderData.discount_rate / 100)
  const commissionFee = subTotal * commissionRate

  return Number(parseFloat(commissionFee).toFixed(2))
}

const getOrderDeliveryCommissionFee = orderData => {
  const deliveyFee = Number(orderData.delivery_fee)
  const commissionRate = orderData.deliverer_fee_rate / 100 || 0.1
  const commissionFee = deliveyFee * commissionRate

  return Number(parseFloat(commissionFee).toFixed(2))
}

const getExpressOrderSummary = order => {
  return {
    ...order,
  }
}

const filterByRegion = (orders, region) => {
  const regionOrders = {}
  const orderKeys = Object.keys(orders)
  const orderCount = orderKeys.length

  for (let index = 0; index < orderCount; index += 1) {
    const orderKey = orderKeys[index]
    const order = orders[orderKey]
    const orderRegion = order.region || 'pnh'

    if (region === orderRegion) regionOrders[orderKey] = order
  }

  return regionOrders
}

const serializePromotionExpense = orders => {
  const orderCount = orders.length

  let subtotal = 0
  let commission = 0
  let promotionDiscount = 0
  let merchantExpense = 0
  let companyExpense = 0
  const merchants = {}

  for (let i = 0; i < orderCount; i += 1) {
    const order = orders[i]
    const orderCompanyDiscount = order.costSharing ? order.costSharing.company_discount : 0
    const orderMerchantDiscount = order.costSharing
      ? order.costSharing.merchant_discount
      : order.promotionDiscount
    const orderCommission = (order.subtotal * order.commissionRate) / 100

    subtotal += order.subtotal
    commission += orderCommission
    companyExpense += orderCompanyDiscount
    merchantExpense += orderMerchantDiscount
    promotionDiscount += order.promotionDiscount

    merchants[order.merchantId] = merchants[order.merchantId] || { name: order.merchantName, branches: {} }
    merchants[order.merchantId].branches[order.branchId] = merchants[order.merchantId].branches[
      order.branchId
    ] || {
      name: order.branchName,
      orderCount: 0,
      subtotal: 0,
      commission: 0,
      merchantExpense: 0,
      promotionDiscount: 0,
      companyExpense: 0,
    }

    merchants[order.merchantId].branches[order.branchId].orderCount += 1
    merchants[order.merchantId].branches[order.branchId].subtotal += order.subtotal
    merchants[order.merchantId].branches[order.branchId].commission += orderCommission
    merchants[order.merchantId].branches[order.branchId].merchantExpense += orderMerchantDiscount
    merchants[order.merchantId].branches[order.branchId].companyExpense += orderCompanyDiscount
    merchants[order.merchantId].branches[order.branchId].promotionDiscount += order.promotionDiscount
  }

  return {
    orderCount,
    subtotal,
    commission,
    promotionDiscount,
    merchantExpense,
    companyExpense,
    merchants,
  }
}

const getSmallOrderFee = (subtotal, deliveryFeeOffer, order) => {
  const isShouldChargeFee = _.get(deliveryFeeOffer, 'small_order_fee', false)
  const deliveryFee = _.get(order, 'delivery_fee', 0)

  if (!isShouldChargeFee) return 0

  let deliveryDiscountOffer = Number(_.get(deliveryFeeOffer, 'delivery_discount', 0))
  deliveryDiscountOffer = deliveryDiscountOffer > deliveryFee ? deliveryFee : deliveryDiscountOffer

  const profitMargin = Number(_.get(deliveryFeeOffer, 'profit_margin', 0))

  const branchCommissionRate = _.get(order, 'restaurant.branch.commission_rate', 0)
  const merchantCommissionRate = _.get(order, 'discount_rate', 0)

  const commissionRate = branchCommissionRate || merchantCommissionRate
  const commissionFee = Number(subtotal * (commissionRate / 100))

  const earnProfit = commissionFee - deliveryDiscountOffer
  if (earnProfit >= 0) return 0

  let smallOrderFee = Math.abs(earnProfit) // convert from negative value to positive value

  if (profitMargin > 0) {
    const profit = subtotal * (profitMargin / 100)
    smallOrderFee += profit
  }

  return Number(parseFloat(smallOrderFee).toFixed(2))
}

const getGrandTotalBeforeVoucherDiscount = (prevOrder, promotionDiscount, fees, items) => {
  if (!prevOrder) return null

  const smallOrderFee = _.get(fees, 'smallOrderFee', 0)
  const markedUpFee = _.get(fees, 'markedUpFee', 0)

  const { restaurant } = prevOrder
  let dynamicFees = _.get(restaurant, 'branch.dynamic_fees', {})
  dynamicFees = serializeDynamicFees(dynamicFees)

  const noChangeDynamicFees = dynamicFees.allFees.totalAmount - smallOrderFee - markedUpFee

  const newSubtotal = getOrderSubTotal(items)
  const vatRate =  _.get(restaurant, 'vat', 0)
  const vatAmount = convertToNumber(newSubtotal * (vatRate / 100))

  const grandTotalDeliveryFee = Number(prevOrder.delivery_fee - prevOrder.delivery_discount)

  const grandTotalDynamicsFee = markedUpFee + smallOrderFee + noChangeDynamicFees

  let grandTotal = newSubtotal
  grandTotal += vatAmount
  grandTotal -= promotionDiscount
  grandTotal += grandTotalDeliveryFee
  grandTotal += grandTotalDynamicsFee
  grandTotal = Number(grandTotal)

  return grandTotal
}


export default {
  getLatency,
  getEta,
  getOrderItems,
  getOrderDiscount,
  getOrderSummary,
  convertToRiel,
  getOrderStatus,
  hasAttentionScheduleOrders,
  getOrderNumber,
  getOrderSubTotal,
  getGroupOrderSubTotal,
  extractGroupOrderItems,
  getOrderPromotionDiscount,
  countOrderItems,
  countGroupOrderItems,
  getOrderCommissionFee,
  getOrderDeliveryCommissionFee,
  serializeDynamicFees,
  getExpressOrderSummary,
  getExpressOrderNumber,
  getItemDiscountRate,
  serializedBranchPromotion,
  serializePromotionExpense,
  getOrderSubtotalWithDiscount,
  convertToNumber,
  filterByRegion,
  calculateCustomerGrandTotal,
  getGrandTotalBeforeVoucherDiscount,
  getSmallOrderFee,
}



// WEBPACK FOOTER //
// ./src/utils/order.js